import { userAtom } from '@/atoms/user.atom';
import { batchStatusMap, formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { useAlert } from '@/providers/AlertProvider';
import { deleteBatchMutation, getBatch, resetBatchMutation } from '@/queries/attachment.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { RotateCcw, Trash2 } from 'lucide-react';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { toast } from 'sonner';
import AttachmentList from './AttachmentList';
import ByproductList from './ByproductList';
import Header from './Header';
import JobList from './JobList';

const formatBytesShort = (bytes?: number | null): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

interface iindexBatchPage {}

const BatchPage: FunctionComponent<iindexBatchPage> = () => {
  const { t } = useTranslation('project');
  const [shouldPoll, setShouldPoll] = useState(false);
  const user = useAtomValue(userAtom);
  const isActegonUser = (user?.email?.endsWith('@actegon.com') || user?.email?.endsWith('@skygis.cloud')) ?? false;
  const { href, project, projectLoading } = useOutletContext<{
    href: string;
    project: Project;
    projectLoading: boolean;
  }>();
  const { batchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();

  const deleteBatch = useMutation({
    mutationFn: () => execute(deleteBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Deleted');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('batch.batchDeleted'));
      navigate(`${href}/upload`);
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedDeleteBatch'));
    },
  });

  const resetBatch = useMutation({
    mutationFn: () => execute(resetBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Reset');
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('batch.batchReset'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedResetBatch'));
    },
  });

  const handleDelete = () => {
    openAlert({
      title: t('batch.deleteBatchTitle'),
      content: t('batch.deleteBatchDescription'),
      actions: [{ label: t('batch.delete'), variant: 'destructive', onClick: () => deleteBatch.mutate() }],
    });
  };

  const handleReset = () => {
    openAlert({
      title: t('batch.resetBatchTitle'),
      content: t('batch.resetBatchDescription'),
      actions: [{ label: t('batch.reset'), variant: 'destructive', onClick: () => resetBatch.mutate() }],
    });
  };

  const { data: batchData, isLoading } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => execute(getBatch, { id: batchId! }),
    refetchInterval: shouldPoll ? 500 : false,
  });

  const batch = batchData?.batch;
  usePageMeta({
    title: [batch?.key ? t('batch.batchKey', { key: batch.key.toUpperCase() }) : undefined, project?.name],
  });

  useEffect(() => {
    setShouldPoll(batch?.status === 'PROCESSING' || batch?.status === 'UPLOADING');
  }, [batch?.status]);

  if (!batch) return null;
  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : project.name, href },
        { label: t('batch.uploadFiles'), href: `${href}/upload` },
        {
          label: isLoading ? t('edit.loading') : t('batch.batchKey', { key: batch?.key?.toUpperCase() }),
          href: `${href}/batch/${batchId}`,
        },
      ]}>
      <div className='max-w-screen-md px-4 mx-auto w-full pb-12 pt-12'>
        <Header status={batch?.status} srid={batch.srid || project.srid} attachments={batch?.attachments ?? []} />
        <div className='border rounded-md border-slate-200/70 mt-0 bg-white dark:bg-stone-900 dark:border-stone-800 px-4 py-2.5 items-start gap-4 grid grid-cols-4'>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.status')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1'>
              {batchStatusMap[batch?.status].icon}
              {batchStatusMap[batch?.status].label}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>{t('batch.label')}</span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              {t('batch.batchKey', { key: batch?.key?.toUpperCase() })}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedBy')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1.5'>
              <img
                src={
                  batch?.createdBy?.avatar?.url ||
                  getPlaceholderAvatar(batch?.createdBy?.email || batch?.createdBy?.id || '')
                }
                alt={batch?.createdBy?.name || t('batch.unknown')}
                className='size-4 rounded-full'
              />
              {batch?.createdBy?.name}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedAt')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              {formatDateTime(batch?.createdAt ?? new Date())}
            </span>
          </div>
        </div>
        {batch.kind === 'ANALYSIS' ? (
          <div className='mt-2 text-xs text-slate-500 dark:text-stone-400 flex items-center gap-2'>
            <span className='inline-flex items-center rounded-full border border-violet-200/70 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-300'>
              {t('batchList.kindAnalysis', { ns: 'components' })}
            </span>
            <span>{t('batch.byproducts.analysisHint')}</span>
            <span className='ml-auto'>
              {t('batch.byproducts.totalSize', { size: formatBytesShort(batch.totalBytes) })}
            </span>
          </div>
        ) : (
          <div className='mt-2 text-xs text-slate-500 dark:text-stone-400 text-right'>
            {t('batch.byproducts.totalSize', { size: formatBytesShort(batch.totalBytes) })}
          </div>
        )}
        {batch.kind === 'ANALYSIS' ? (
          <ByproductList byproducts={batch.byproducts ?? []} />
        ) : (
          <AttachmentList attachments={batch?.attachments ?? []} batchStatus={batch?.status} />
        )}
        <JobList jobs={batch?.jobs ?? []} />
        <div className='mt-6 flex justify-end gap-2'>
          {isActegonUser && (
            <Button variant='outline' onClick={handleReset} disabled={resetBatch.isPending}>
              <RotateCcw className='size-4 mr-2' />
              {t('batch.resetBatch')}
            </Button>
          )}
          <Button variant='destructive' onClick={handleDelete} disabled={deleteBatch.isPending}>
            <Trash2 className='size-4 mr-2' />
            {t('batch.deleteBatch')}
          </Button>
        </div>
      </div>
    </PageSkeleton>
  );
};

export default BatchPage;
